import React, { useEffect, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getOntologyCoverage, normalizeCoverage } from '../../api/ontologyApi';
import { coverageFindingHref, displayName, hrefForImpactRow } from './ontologyFormat';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The coverage findings that belong to ONE browse tab, shown above its table.
 *
 * The Coverage tab stays where it is — it is a worklist ("where do I start"), not a
 * property of a record, and four of the ten findings are about AGENTS, which have no tab
 * in this section at all. Dissolving the report into the resource tabs would delete that
 * half of it from the product.
 *
 * What it was missing is the other direction: somebody tuning thresholds should not have
 * to remember to visit a separate tab to learn that three of them cite a policy that no
 * longer exists. This puts the relevant subset where they are already looking.
 *
 * It SUMMARISES rather than reprints. A finding of sixty-eight names six of them and links
 * the rest to the Coverage tab: this is a strip above someone else's table, and a table is
 * not improved by fifty codes stacked over it. The Coverage tab is where the whole list is
 * read and worked through, and it renders each row named, typed and linked to the record.
 *
 * Driven by the SAME /ontology/coverage response the tab reads, filtered client-side, so a
 * banner count and the Coverage tab cannot disagree. The alternative — a per-kind count
 * endpoint — is exactly the shape that lets two numbers for one question drift apart, and
 * the control plane already refuses it elsewhere for the same reason (facets() and list()
 * share one query builder so a rail count and a paginator total must agree).
 */

/**
 * Which findings are ABOUT which tab's records.
 *
 * The server's `group` field splits library from agents, which is what the report's two
 * columns need; it does not say which of the three library collections a finding concerns.
 * That mapping is a client concern — only the client has tabs — and is written out here
 * rather than derived from the id prefix, because `controls_without_threshold` is a
 * finding about CONCEPTS whose name mentions neither.
 *
 * A finding id missing from this table simply never appears in a banner. That is the right
 * default for one added server-side: it still shows on the Coverage tab.
 */
const FINDINGS_BY_TAB: Record<string, string[]> = {
    concepts: ['controls_without_threshold'],
    policies: ['policies_governing_nothing', 'policies_with_dangling_concepts'],
    parameters: [
        'parameters_with_dangling_concept',
        'parameters_with_dangling_policy',
        'parameters_without_policy',
    ],
    documents: ['documents_cited_by_nothing', 'citations_to_unregistered_documents'],
};

interface Finding {
    id: string;
    severity: 'warning' | 'info';
    title: string;
    explanation: string;
    count: number;
    items: { kind: string; code: string; label: string; detail?: string }[];
    truncated?: boolean;
}

/**
 * How many records the banner names before it stops naming them.
 *
 * This is a strip above somebody else's table, not the report — its job is to say what kind
 * of gap is outstanding and how big it is, in a shape that can be read at a glance. Printing
 * every item the server sent turned "68 controls have no threshold" into fifty lines of
 * snake_case that nobody reads and that pushes the actual table off the screen.
 *
 * Six is two rows of the three-column grid below: enough to recognise the KIND of thing
 * that is missing ("these are all model hyper-parameters") without becoming the page.
 */
const PREVIEW_ITEMS = 6;

/**
 * One named record in the preview.
 *
 * The name leads and the code follows in the smaller mono face, which is the same order
 * the Coverage tab and every browse table use — the reader looks for a name, and reaches
 * for the code only when they need to search or file for it.
 */
const PreviewItem: React.FC<{ item: Finding['items'][number] }> = ({ item }) => {
    const href = hrefForImpactRow(item.kind, item.code);
    return (
        <Box
            component={href ? 'a' : 'div'}
            href={href ?? undefined}
            title={item.detail ? `${item.code} → ${item.detail}` : item.code}
            sx={{
                display: 'block', minWidth: 0, textDecoration: 'none',
                borderRadius: '4px', px: 1, py: 0.5,
                border: `1px solid ${dsVar('warning-line')}`, backgroundColor: dsVar('warning-soft'),
                ...(href && { '&:hover': { backgroundColor: dsVar('warning', 0.08), borderColor: dsVar('warning', 0.35) } }),
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.75rem', lineHeight: 1.4,
                    color: href ? dsVar('accent-fg') : ink('#E9C97E'),
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
            >
                {displayName(item.label, item.code)}
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.625rem', lineHeight: 1.5, color: ink('#96733A'),
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
            >
                {item.code}
            </Typography>
        </Box>
    );
};

const CoverageBanner: React.FC<{ tab: string }> = ({ tab }) => {
    const [findings, setFindings] = useState<Finding[]>([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const wanted = FINDINGS_BY_TAB[tab];
        if (!wanted) return undefined;

        const controller = new AbortController();
        let active = true;

        (async () => {
            try {
                const report = normalizeCoverage(
                    await getOntologyCoverage({ signal: controller.signal }));
                if (!active) return;
                setFindings(
                    (report.findings as Finding[])
                        .filter((f) => wanted.includes(f.id) && f.count > 0));
            } catch {
                // Deliberately silent, exactly as the landing page's counts are. The table
                // below is the screen; an error banner about an advisory the user did not
                // ask for would be noise over a working list.
                if (active) setFindings([]);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [tab]);

    // Zero findings is the good news, and it is already said on the Coverage tab. Saying
    // "nothing to review" above every clean table would be a permanent empty banner.
    if (findings.length === 0) return null;

    const total = findings.reduce((sum, f) => sum + f.count, 0);

    return (
        <Box
            sx={{
                mb: 2.5, borderRadius: '6px',
                border: `1px solid ${dsVar('warning-line')}`, backgroundColor: dsVar('warning-soft'),
            }}
        >
            <Box
                onClick={() => setOpen((v) => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 1.25, cursor: 'pointer', userSelect: 'none',
                }}
            >
                {open
                    ? <ExpandMoreIcon sx={{ fontSize: 18, color: ink('#B4884A') }} />
                    : <ChevronRightIcon sx={{ fontSize: 18, color: ink('#B4884A') }} />}
                <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: ink('#F59E0B') }} />
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#E9C97E') }}>
                    {total} to review here
                    <Typography component="span" sx={{ fontSize: '0.8125rem', color: ink('#B4884A') }}>
                        {' — '}
                        {findings.map((f) => f.title.toLowerCase()).join('; ')}
                    </Typography>
                </Typography>
            </Box>

            <Collapse in={open} unmountOnExit>
                <Box sx={{ px: 1.5, pb: 1.5, pl: 4.5 }}>
                    {findings.map((finding) => {
                        const preview = finding.items.slice(0, PREVIEW_ITEMS);
                        // The COUNT is the truth. `items` is already a server-side sample on
                        // a large finding, so "and 62 more" has to be measured against the
                        // count rather than against the length of what arrived — otherwise a
                        // truncated response would quietly under-report the backlog.
                        const remaining = Math.max(finding.count - preview.length, 0);

                        return (
                            <Box key={finding.id} sx={{ mt: 1.5 }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: ink('#E9C97E'), fontWeight: 500 }}>
                                    {finding.title} · {finding.count}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: ink('#B4884A'), mt: 0.25, lineHeight: 1.55 }}>
                                    {finding.explanation}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'grid', gap: 0.75, mt: 1,
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, minmax(0, 1fr))',
                                            lg: 'repeat(3, minmax(0, 1fr))',
                                        },
                                    }}
                                >
                                    {preview.map((item) => (
                                        <PreviewItem key={`${item.kind}-${item.code}`} item={item} />
                                    ))}
                                </Box>

                                {/* The way out of a sample and into the worklist. The
                                    Coverage tab already renders every finding named, linked
                                    and grouped, and each row from there opens the record
                                    where the gap is actually closed — so this hands the job
                                    over rather than growing a second copy of the report
                                    inside a banner. */}
                                <Box
                                    component="a"
                                    href={coverageFindingHref(finding.id)}
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        mt: 1, fontSize: '0.75rem', color: dsVar('accent-fg'),
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {remaining > 0
                                        ? `Review all ${finding.count} in Coverage — ${remaining} more not shown`
                                        : 'Review these in Coverage'}
                                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Collapse>

        </Box>
    );
};

export default CoverageBanner;
